/**
 * Credit Rewards Promotion Templates
 * Gamified credit-based engagement promotions
 */

import { PromotionTemplate } from "./types";

export const CREDIT_REWARDS_TEMPLATES: PromotionTemplate[] = [
  {
    id: "daily-login-bonus",
    name: "Daily Login Bonus",
    category: "credit_rewards",
    description: "Reward users with credits for daily logins to boost retention",
    strategy: "Daily login bonuses create habit-forming behavior. Users return daily to claim credits, increasing engagement and lifetime value. Works best with streak multipliers.",
    icon: "CalendarOutlined",
    color: "#10B981",
    expectedOutcomes: [
      "Increased daily active users",
      "Habit formation and retention",
      "Higher engagement metrics",
    ],
    bestPractices: [
      "Keep base reward small but meaningful",
      "Add streak multipliers for consecutive days",
      "Show progress toward next milestone",
      "Send reminder notifications",
    ],
    tags: ["credits", "daily", "retention", "engagement"],
    bestFor: ["SaaS products", "Mobile apps", "Community platforms"],
    difficulty: "beginner",
    setupTime: "10 min",
    defaults: {
      type: "popup",
      displayStyle: "festive",
      priority: 7,
      targetAudience: ["all"],
      discount: {
        discountType: "fixed_amount",
        discountValue: 5,
        currency: "credits",
        applicablePlans: ["all"],
      },
      styling: {
        theme: "gradient_green",
        primaryColor: "#10B981",
        secondaryColor: "#059669",
        animation: "bounce",
      },
      displayConditions: {
        showOnPages: ["dashboard", "widget", "popup"],
        maxImpressionsPerUser: 1,
        delaySeconds: 1,
      },
      urgency: {
        showCountdown: false,
        stockLimit: 0,
      },
      gamification: {
        type: "none",
      },
      content: {
        en: {
          headline: "Daily Bonus!",
          subheadline: "You've earned 5 credits for logging in today!",
          ctaText: "Claim Credits",
          ctaSecondaryText: "View Balance",
          badge: "DAILY REWARD",
          terms: "*Login daily to build your streak and earn more.",
        },
      },
    },
  },
  {
    id: "streak-milestone",
    name: "Streak Milestone Reward",
    category: "credit_rewards",
    description: "Celebrate login streaks with bonus credit rewards",
    strategy: "Streak rewards create powerful retention loops. Users are motivated to maintain their streak, reducing churn. The fear of losing progress is a strong motivator.",
    icon: "FireOutlined",
    color: "#EF4444",
    expectedOutcomes: [
      "Reduced churn rate",
      "Increased daily engagement",
      "Strong user habit formation",
    ],
    bestPractices: [
      "Offer escalating rewards for longer streaks",
      "Add grace period for missed days",
      "Show streak status prominently",
      "Celebrate milestones with special animations",
    ],
    tags: ["credits", "streak", "retention", "milestone", "gamification"],
    bestFor: ["Apps with daily use cases", "Learning platforms", "Fitness apps"],
    difficulty: "intermediate",
    setupTime: "15 min",
    defaults: {
      type: "popup",
      displayStyle: "bold",
      priority: 9,
      targetAudience: ["all"],
      discount: {
        discountType: "fixed_amount",
        discountValue: 25,
        currency: "credits",
        applicablePlans: ["all"],
      },
      styling: {
        theme: "gradient_orange",
        primaryColor: "#EF4444",
        secondaryColor: "#F97316",
        animation: "bounce",
      },
      displayConditions: {
        showOnPages: ["dashboard"],
        maxImpressionsPerUser: 1,
        delaySeconds: 0,
      },
      urgency: {
        showCountdown: false,
        stockLimit: 0,
      },
      gamification: {
        type: "streak",
        streakDays: 7,
        resetOnMiss: true,
        streakColor: "#F59E0B",
        streakRewards: [
          { day: 1, reward: "Day 1 Start", value: 5, type: "credits" },
          { day: 3, reward: "Day 3 Momentum", value: 10, type: "credits" },
          { day: 5, reward: "Day 5 Champion", value: 15, type: "credits" },
          { day: 7, reward: "Day 7 Master", value: 25, type: "credits" },
        ],
      },
      content: {
        en: {
          headline: "7 Day Streak!",
          subheadline: "You're on fire! Here's 25 bonus credits for your dedication.",
          ctaText: "Claim Bonus",
          ctaSecondaryText: "View Streak",
          badge: "STREAK BONUS",
          terms: "*Keep your streak alive to unlock bigger rewards.",
        },
      },
    },
  },
  {
    id: "spin-wheel-credits",
    name: "Spin the Wheel",
    category: "credit_rewards",
    description: "Gamified spin wheel for credit prizes",
    strategy: "Spin wheels create excitement and anticipation. The randomness triggers dopamine responses, making users more engaged. Great for weekly bonus opportunities.",
    icon: "SyncOutlined",
    color: "#8B5CF6",
    expectedOutcomes: [
      "High engagement rates",
      "Viral sharing potential",
      "Increased time in app",
    ],
    bestPractices: [
      "Balance prize distribution (more small wins)",
      "Add visual excitement with animations",
      "Limit plays to create anticipation",
      "Share winning moments on social",
    ],
    tags: ["credits", "gamification", "spin-wheel", "engagement", "fun"],
    bestFor: ["Gaming apps", "E-commerce", "Entertainment platforms"],
    difficulty: "intermediate",
    setupTime: "20 min",
    defaults: {
      type: "gamified",
      displayStyle: "bold",
      priority: 8,
      targetAudience: ["all"],
      discount: {
        discountType: "fixed_amount",
        discountValue: 50,
        currency: "credits",
        applicablePlans: ["all"],
      },
      styling: {
        theme: "gradient_purple",
        primaryColor: "#8B5CF6",
        secondaryColor: "#A855F7",
        animation: "spin",
      },
      displayConditions: {
        showOnPages: ["dashboard"],
        maxImpressionsPerUser: 1,
        delaySeconds: 2,
      },
      urgency: {
        showCountdown: false,
        stockLimit: 0,
      },
      gamification: {
        type: "spin_wheel",
        wheelSegments: [
          { label: "100 Credits!", value: 100, probability: 5 },
          { label: "50 Credits", value: 50, probability: 15 },
          { label: "25 Credits", value: 25, probability: 30 },
          { label: "10 Credits", value: 10, probability: 40 },
          { label: "Try Again", value: 0, probability: 10 },
        ],
      },
      content: {
        en: {
          headline: "Spin to Win!",
          subheadline: "Your weekly spin is ready. Win up to 100 credits!",
          ctaText: "Spin Now",
          ctaSecondaryText: "Skip",
          badge: "WEEKLY SPIN",
          terms: "*One free spin per week. Results are random.",
        },
      },
    },
  },
  {
    id: "scratch-card-credits",
    name: "Scratch Card Surprise",
    category: "credit_rewards",
    description: "Interactive scratch card for credit prizes",
    strategy: "Scratch cards combine anticipation with interactivity. The physical act of scratching creates engagement and the reveal moment is highly satisfying.",
    icon: "CreditCardOutlined",
    color: "#F59E0B",
    expectedOutcomes: [
      "High interaction rates",
      "Memorable user experience",
      "Increased session duration",
    ],
    bestPractices: [
      "Make scratching feel satisfying",
      "Reveal prize gradually",
      "Add sound effects for wins",
      "Offer consolation prizes",
    ],
    tags: ["credits", "gamification", "scratch-card", "interactive", "surprise"],
    bestFor: ["Promotional campaigns", "Special events", "User milestones"],
    difficulty: "intermediate",
    setupTime: "20 min",
    defaults: {
      type: "gamified",
      displayStyle: "bold",
      priority: 8,
      targetAudience: ["all"],
      discount: {
        discountType: "fixed_amount",
        discountValue: 40,
        currency: "credits",
        applicablePlans: ["all"],
      },
      styling: {
        theme: "gradient_yellow",
        primaryColor: "#F59E0B",
        secondaryColor: "#FBBF24",
        animation: "fade",
      },
      displayConditions: {
        showOnPages: ["dashboard"],
        maxImpressionsPerUser: 1,
        delaySeconds: 2,
      },
      urgency: {
        showCountdown: false,
        stockLimit: 0,
      },
      gamification: {
        type: "scratch_card",
        scratchThreshold: 60,
        scratchPercentage: 60,
        revealAnimation: "fade",
        backgroundPattern: "dots",
        scratchColor: "#C0C0C0",
        prizeDisplayText: "You won credits!",
      },
      content: {
        en: {
          headline: "Mystery Scratch Card!",
          subheadline: "Scratch to reveal your credit prize!",
          ctaText: "Scratch Now",
          ctaSecondaryText: "Skip",
          badge: "SURPRISE",
          terms: "*Scratch the card to reveal your prize.",
        },
      },
    },
  },
  {
    id: "slot-machine-jackpot",
    name: "Slot Machine Jackpot",
    category: "credit_rewards",
    description: "Vegas-style slot machine for credit prizes",
    strategy: "Slot machines are the ultimate gamification tool. The spinning reels, near-misses, and jackpot potential create an exciting experience users love.",
    icon: "DollarOutlined",
    color: "#EC4899",
    expectedOutcomes: [
      "Maximum engagement",
      "Viral potential",
      "High user satisfaction",
    ],
    bestPractices: [
      "Use eye-catching symbols",
      "Add near-miss mechanics",
      "Celebrate jackpots dramatically",
      "Balance win frequency",
    ],
    tags: ["credits", "gamification", "slots", "jackpot", "vegas"],
    bestFor: ["Gaming platforms", "Loyalty programs", "Special promotions"],
    difficulty: "advanced",
    setupTime: "25 min",
    defaults: {
      type: "gamified",
      displayStyle: "bold",
      priority: 8,
      targetAudience: ["all"],
      discount: {
        discountType: "fixed_amount",
        discountValue: 150,
        currency: "credits",
        applicablePlans: ["all"],
      },
      styling: {
        theme: "gradient_pink",
        primaryColor: "#EC4899",
        secondaryColor: "#F472B6",
        animation: "bounce",
      },
      displayConditions: {
        showOnPages: ["dashboard"],
        maxImpressionsPerUser: 1,
        delaySeconds: 2,
      },
      urgency: {
        showCountdown: false,
        stockLimit: 0,
      },
      gamification: {
        type: "slot_machine",
        wheelSegments: [
          { label: "JACKPOT! 150 Credits!", value: 150, probability: 2 },
          { label: "60 Credits!", value: 60, probability: 8 },
          { label: "30 Credits", value: 30, probability: 20 },
          { label: "15 Credits", value: 15, probability: 35 },
          { label: "No Match", value: 0, probability: 35 },
        ],
      },
      content: {
        en: {
          headline: "Hit the Jackpot!",
          subheadline: "Pull the lever and win up to 150 credits!",
          ctaText: "Pull Lever",
          ctaSecondaryText: "Skip",
          badge: "JACKPOT",
          terms: "*Spin the slots for your chance to win.",
        },
      },
    },
  },
  {
    id: "achievement-unlock",
    name: "Achievement Unlock",
    category: "credit_rewards",
    description: "Reward users with credits for unlocking achievements",
    strategy: "Achievement systems tap into completionist psychology. Users are motivated to earn all badges and the credit rewards make achievements tangible.",
    icon: "TrophyOutlined",
    color: "#F59E0B",
    expectedOutcomes: [
      "Increased feature adoption",
      "Higher engagement across features",
      "User progression and loyalty",
    ],
    bestPractices: [
      "Create varied achievement types",
      "Show progress toward achievements",
      "Make badges visually appealing",
      "Celebrate unlocks prominently",
    ],
    tags: ["credits", "achievements", "badges", "progression", "engagement"],
    bestFor: ["Feature-rich apps", "Learning platforms", "Productivity tools"],
    difficulty: "intermediate",
    setupTime: "15 min",
    defaults: {
      type: "popup",
      displayStyle: "festive",
      priority: 9,
      targetAudience: ["all"],
      discount: {
        discountType: "fixed_amount",
        discountValue: 50,
        currency: "credits",
        applicablePlans: ["all"],
      },
      styling: {
        theme: "gradient_yellow",
        primaryColor: "#F59E0B",
        secondaryColor: "#EAB308",
        animation: "bounce",
      },
      displayConditions: {
        showOnPages: ["all"],
        maxImpressionsPerUser: 1,
        delaySeconds: 0,
      },
      urgency: {
        showCountdown: false,
        stockLimit: 0,
      },
      gamification: {
        type: "none",
      },
      content: {
        en: {
          headline: "Achievement Unlocked!",
          subheadline: "Power User - You've made 100 queries! Here's 50 credits.",
          ctaText: "Claim Reward",
          ctaSecondaryText: "View Achievements",
          badge: "ACHIEVEMENT",
          terms: "*One-time reward for each achievement.",
        },
      },
    },
  },
  {
    id: "referral-credits",
    name: "Referral Credit Bonus",
    category: "credit_rewards",
    description: "Double-sided credit rewards for referrals",
    strategy: "Credit-based referral programs are highly effective. Both referrer and referee get credits, creating a win-win that drives organic growth.",
    icon: "UsergroupAddOutlined",
    color: "#10B981",
    expectedOutcomes: [
      "Viral growth",
      "Lower customer acquisition cost",
      "Higher quality leads",
    ],
    bestPractices: [
      "Make both sides win",
      "Credit immediately on signup",
      "Show referral leaderboard",
      "Offer bonus for multiple referrals",
    ],
    tags: ["credits", "referral", "viral", "growth", "sharing"],
    bestFor: ["B2C products", "Social apps", "Freemium models"],
    difficulty: "beginner",
    setupTime: "15 min",
    defaults: {
      type: "popup",
      displayStyle: "standard",
      priority: 6,
      targetAudience: ["all"],
      discount: {
        discountType: "fixed_amount",
        discountValue: 100,
        currency: "credits",
        applicablePlans: ["all"],
      },
      styling: {
        theme: "gradient_green",
        primaryColor: "#10B981",
        secondaryColor: "#34D399",
        animation: "fade",
      },
      displayConditions: {
        showOnPages: ["dashboard"],
        maxImpressionsPerUser: 3,
        delaySeconds: 5,
      },
      urgency: {
        showCountdown: false,
        stockLimit: 0,
      },
      gamification: {
        type: "none",
      },
      content: {
        en: {
          headline: "Invite Friends, Earn Credits!",
          subheadline: "Get 100 credits for each friend who joins. They get 50 too!",
          ctaText: "Share Invite Link",
          ctaSecondaryText: "Maybe Later",
          badge: "REFERRAL",
          terms: "*Credits awarded when your friend creates an account.",
        },
      },
    },
  },
  {
    id: "first-purchase-credits",
    name: "First Purchase Bonus",
    category: "credit_rewards",
    description: "Bonus credits for first-time purchasers",
    strategy: "First purchase bonuses reduce friction for converting free users. The bonus credits give them more value and encourage continued engagement.",
    icon: "GiftOutlined",
    color: "#3B82F6",
    expectedOutcomes: [
      "Higher conversion rate",
      "Better first-time experience",
      "Increased customer lifetime value",
    ],
    bestPractices: [
      "Make bonus substantial",
      "Credit immediately after purchase",
      "Highlight the bonus value",
      "Follow up with how to use credits",
    ],
    tags: ["credits", "first-purchase", "conversion", "bonus", "welcome"],
    bestFor: ["Freemium products", "E-commerce", "Subscription services"],
    difficulty: "beginner",
    setupTime: "10 min",
    defaults: {
      type: "popup",
      displayStyle: "festive",
      priority: 8,
      targetAudience: ["free"],
      discount: {
        discountType: "fixed_amount",
        discountValue: 200,
        currency: "credits",
        applicablePlans: ["all"],
      },
      styling: {
        theme: "gradient_blue",
        primaryColor: "#3B82F6",
        secondaryColor: "#60A5FA",
        animation: "bounce",
      },
      displayConditions: {
        showOnPages: ["pricing", "checkout"],
        maxImpressionsPerUser: 5,
        delaySeconds: 3,
      },
      urgency: {
        showCountdown: false,
        stockLimit: 0,
      },
      gamification: {
        type: "none",
      },
      content: {
        en: {
          headline: "First Purchase Bonus!",
          subheadline: "Subscribe now and get 200 bonus credits instantly!",
          ctaText: "Get Bonus Credits",
          ctaSecondaryText: "View Plans",
          badge: "BONUS",
          terms: "*200 credits added instantly after first purchase.",
        },
      },
    },
  },
];
